import React from "react"
import FrontmatterPageContext from "~context/FakePageContext"

// https://storybook.staging.secretcdn.net/?path=/docs/components-sidemenu--example#sidemenu
import { SideMenuItem, SideMenuContent, SideMenuTrigger, SideMenuTriggerLink } from "@fastly/consistently-vanilla"

import { SideMenuProducts } from "./side-menu-products"
import { SideMenuAccountEN } from "./side-menu-account-en"
import { SideMenuAccountJA } from "./side-menu-account-ja"
import { SideMenuComputeEN } from "./side-menu-compute-en"
import { SideMenuComputeJA } from "./side-menu-compute-ja"
import { SideMenuFullsitedeliveryEN } from "./side-menu-full-site-delivery-en"
import { SideMenuFullsitedeliveryJA } from "./side-menu-full-site-delivery-ja"
import { SideMenuGettingstartedEN } from "./side-menu-getting-started-en"
import { SideMenuGettingstartedJA } from "./side-menu-getting-started-ja"
import { SideMenuObservabilityEN } from "./side-menu-observability-en"
import { SideMenuObservabilityJA } from "./side-menu-observability-ja"
import { SideMenuIntegrationsEN } from "./side-menu-integrations-en"
import { SideMenuIntegrationsJA } from "./side-menu-integrations-ja"
import { SideMenuSecurityEN } from "./side-menu-security-en"
import { SideMenuSecurityJA } from "./side-menu-security-ja"
import { SideMenuNgwafEN } from "./side-menu-ngwaf-en"
import { SideMenuNgwafJA } from "./side-menu-ngwaf-ja"
import { levelToFrontmatterDescriptor, levelToNav, levelToSelector, MENU_INDENTATION } from "./side-menu.consts"

export const SideMenuSwitcher = () => {
  const frontmatter = React.useContext(FrontmatterPageContext)

  switch (frontmatter.section_safe) {
    case "products":
      return <SideMenuProducts />
    case "ngwaf":
      switch (frontmatter.lang || "en") {
        default:
        case "en":
          return <SideMenuNgwafEN />
        case "ja":
          return <SideMenuNgwafJA />
      }
    case "account":
      switch (frontmatter.lang || "en") {
        default:
        case "en":
          return <SideMenuAccountEN />
        case "ja":
          return <SideMenuAccountJA />
      }
    case "compute":
      switch (frontmatter.lang || "en") {
        default:
        case "en":
          return <SideMenuComputeEN />
        case "ja":
          return <SideMenuComputeJA />
      }
    case "full_site_delivery":
      switch (frontmatter.lang || "en") {
        default:
        case "en":
          return <SideMenuFullsitedeliveryEN />
        case "ja":
          return <SideMenuFullsitedeliveryJA />
      }
    case "getting_started":
      switch (frontmatter.lang || "en") {
        default:
        case "en":
          return <SideMenuGettingstartedEN />
        case "ja":
          return <SideMenuGettingstartedJA />
      }
    case "observability":
      switch (frontmatter.lang || "en") {
        default:
        case "en":
          return <SideMenuObservabilityEN />
        case "ja":
          return <SideMenuObservabilityJA />
      }
    case "integrations":
      switch (frontmatter.lang || "en") {
        default:
        case "en":
          return <SideMenuIntegrationsEN />
        case "ja":
          return <SideMenuIntegrationsJA />
      }
    case "security":
      switch (frontmatter.lang || "en") {
        default:
        case "en":
          return <SideMenuSecurityEN />
        case "ja":
          return <SideMenuSecurityJA />
      }
    case "canary":
      return <>🐤</>
    case "none":
      return ""
    default:
      return `--ELSE (${frontmatter.section_safe})--`
  }
}

const renderLeafs = ({ leafs, level, link_prefix, slug_collection }) => {
  const frontmatter = React.useContext(FrontmatterPageContext)

  if (!leafs) {
    return null
  }

  return leafs.map((thisOrphan) => {
    const href = `${link_prefix}${thisOrphan.slug.replace(slug_collection, "")}`
    const currentPage = `${link_prefix}${frontmatter.fileslug}`
    const displayTitle = thisOrphan.frontmatter.nav_title || thisOrphan.frontmatter["nav-title"] || thisOrphan.frontmatter.header
    return (
      <SideMenuItem key={thisOrphan.slug} value={thisOrphan.slug}>
        <SideMenuTriggerLink
          href={href}
          aria-current={href === currentPage && "page"}
          style={{ marginLeft: level * MENU_INDENTATION, whiteSpace: "normal" }}
        >
          {displayTitle}
        </SideMenuTriggerLink>
      </SideMenuItem>
    )
  })
}

const renderBranches = ({ branches, data, link_prefix, slug_collection, level }) => {
  const frontmatter = React.useContext(FrontmatterPageContext)

  const getMenuItems = (parent) =>
    data.guides.group.find((n) => n.fieldValue === parent.crumb)?.nodes.filter((n) => n.frontmatter.unlisted !== true)

  return branches.map((branch) => (
    <SideMenuItem value={branch.crumb} key={branch.crumb}>
      <SideMenuTrigger style={{ marginLeft: level * MENU_INDENTATION }}>
      {(() => {
        const node = data[levelToSelector[level]]?.nodes.find((n) => n.frontmatter.crumb === branch.crumb);
        return node?.frontmatter?.nav_title || node?.frontmatter?.["nav-title"] || node?.frontmatter?.name || branch.crumb;
      })()}
      </SideMenuTrigger>
      <SideMenuContent
        style={{ paddingInlineStart: 0 }}
        defaultValue={[frontmatter[levelToFrontmatterDescriptor[level + 1]]?.frontmatter?.crumb]}
        type="multiple"
      >
        {branch[levelToNav[level + 1]] &&
          renderBranches({
            branches: branch[levelToNav[level + 1]],
            level: level + 1,
            data,
            link_prefix,
            slug_collection,
          })}
        {renderLeafs({
          leafs: getMenuItems(branch),
          level: level + 1,
          slug_collection,
          link_prefix,
        })}
      </SideMenuContent>
    </SideMenuItem>
  ))
}

export function Yaml2Menu(data, navYaml, slug_collection, link_prefix) {
  const frontmatter = React.useContext(FrontmatterPageContext)

  return navYaml.map((toplevel) => {
    if (toplevel?.unlisted) {
      // this primary is marked as unlisted, so we need to skip it...
      if (frontmatter?.primary_nav === toplevel.crumb) {
        // unless the page we are looking at is "in" that primary (wow thats broad), then we... do nothing?
      } else if (frontmatter?.cat_id === toplevel.crumb) {
        // or we are on the primary "landing page" for this top level item
        // (which isnt technically "in" the primary, so the 1st check wont catch it)
      } else {
        // the page is NOT in the unlisted primary, so bail out as we originally wanted to
        return <></>
      }
    }

    const L1meta = data.primarys.nodes.filter((d) => d.frontmatter["cat_id"] === toplevel.crumb)[0].frontmatter
    const myOrphans =
      data.orphans.group
        .filter((groupset) => groupset.fieldValue === toplevel.crumb)[0]
        ?.nodes.filter((n) => n.frontmatter.unlisted !== true) || []

    let showPrimary = 0

    const showableSecondary = toplevel?.["secondary-nav"]
      ? toplevel["secondary-nav"].filter((branch) => {
          if (branch?.unlisted) {
            // this sec nav is marked as unlisted...
            if (branch.crumb === frontmatter?.secondary_nav) {
              // but if on any guide IN that secondary, force it viewable
              return true
            }
            // otherwise honor unlisted and not-list it
            return false
          }
          return true
        })
      : []

    // secondary cards
    let secondaryCards = null
    if (showableSecondary?.length) {
      showPrimary += 1
      secondaryCards = renderBranches({
        branches: showableSecondary,
        data,
        link_prefix,
        slug_collection,
        level: 1,
      })
    }

    let orphansL2 = null
    if (myOrphans.length) {
      showPrimary += 2
      orphansL2 = renderLeafs({
        leafs: myOrphans,
        slug_collection,
        link_prefix,
        level: 1,
      })
    }

    const currentPage = `${link_prefix}${frontmatter.fileslug}`
    const openedMenu = frontmatter.mySecondary?.frontmatter?.crumb

    return (
      <SideMenuItem value={L1meta.cat_id} key={L1meta.cat_id}>
        {showPrimary > 0 ? (
          <>
            <SideMenuTriggerLink
              href={`${link_prefix}${toplevel["crumb"]}`}
              showRevealers={Boolean(orphansL2?.length || secondaryCards)}
              aria-current={`${link_prefix}${toplevel["crumb"]}` === currentPage && "page"}
            >
              {L1meta.nav_title || L1meta.section_name}
            </SideMenuTriggerLink>
            <SideMenuContent style={{ paddingInlineStart: 0 }} defaultValue={[openedMenu]} type="multiple">
              {secondaryCards}
              {orphansL2}
            </SideMenuContent>
          </>
        ) : (
          ""
        )}
      </SideMenuItem>
    )
  })
}